import React from 'react';
import { Page, Text, View, Document, StyleSheet } from '@react-pdf/renderer';
import { Invoice, Customer } from 'types';
import { formatDate } from 'app/utils/formatDate';
import { formatCurrency } from 'app/utils/formatCurrency';

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 11,
    fontFamily: 'Helvetica',
  },
  header: {
    marginBottom: 20,
  },
  section: {
    marginBottom: 10,
  },
  title: {
    fontSize: 20,
    marginBottom: 10,
    fontWeight: 'bold',
  },
  customerInfo: {
    marginBottom: 10,
  },
  invoiceInfo: {
    marginBottom: 10,
  },
  table: {
    width: 'auto',
    marginTop: 20,
  },
  tableRow: {
    flexDirection: 'row',
  },
  tableColHeader: {
    width: '25%',
    borderBottom: '1pt solid #000',
    fontWeight: 'bold',
    paddingBottom: 4,
  },
  tableCol: {
    width: '25%',
    paddingTop: 4,
    paddingBottom: 4,
  },
  statusPaid: {
    color: '#198754',
    fontWeight: 'bold',
  },
  statusUnpaid: {
    color: '#d9534f',
    fontWeight: 'bold',
  },
});

interface InvoicePDFProps {
  invoice: Invoice & { customer: Customer };
}

const InvoicePDF: React.FC<InvoicePDFProps> = ({ invoice }) => {
  const { customer } = invoice;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.title}>Invoice #{invoice.id}</Text>
        </View>

        {/* Customer Info */}
        <View style={styles.customerInfo}>
          <Text>
            {customer.first_name} {customer.last_name}
          </Text>
          <Text>{customer.address}</Text>
          <Text>
            {customer.zip_code} {customer.city}
          </Text>
          <Text>{customer.country}</Text>
        </View>

        {/* Invoice Info */}
        <View style={styles.invoiceInfo}>
          <Text>Date: {formatDate(invoice.date)}</Text>
          <Text>
            Deadline:{' '}
            {invoice.deadline ? formatDate(invoice.deadline) : 'Not set'}
          </Text>
          <Text>
            Status:{' '}
            <Text
              style={invoice.paid ? styles.statusPaid : styles.statusUnpaid}
            >
              {invoice.paid ? 'Paid' : 'Unpaid'}
            </Text>
          </Text>
          {invoice.finalized && <Text>Finalized</Text>}
        </View>

        {/* Table */}
        <View style={styles.table}>
          {/* Table Header */}
          <View style={styles.tableRow}>
            <Text style={styles.tableColHeader}>Description</Text>
            <Text style={styles.tableColHeader}>Quantity</Text>
            <Text style={styles.tableColHeader}>Price</Text>
            <Text style={styles.tableColHeader}>Tax</Text>
          </View>

          {/* Table Rows */}
          {invoice.invoice_lines.map((line) => (
            <View style={styles.tableRow} key={line.id}>
              <Text style={styles.tableCol}>{line.label}</Text>
              <Text style={styles.tableCol}>{line.quantity}</Text>
              <Text style={styles.tableCol}>{formatCurrency(line.price)}</Text>
              <Text style={styles.tableCol}>{formatCurrency(line.tax)}</Text>
            </View>
          ))}

          {/* Table Footer */}
          <View style={styles.tableRow}>
            <Text style={styles.tableCol}></Text>
            <Text style={styles.tableCol}>Total</Text>
            <Text style={styles.tableCol}>{formatCurrency(invoice.total)}</Text>
            <Text style={styles.tableCol}>{formatCurrency(invoice.tax)}</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default InvoicePDF;
